/* Shared integer-money rules. No DOM, storage or Shopify requests. */
(function (root, factory) {
  var core = factory();
  if (typeof module === 'object' && module.exports) module.exports = core;
  else root.EarlyBFCore = core;
})(typeof window === 'object' ? window : globalThis, function () {
  'use strict';
  function fault(code, details) {
    var error = new Error(code);
    error.code = code;
    error.details = details || {};
    return error;
  }
  function money(value) {
    if (!Number.isSafeInteger(value) || value < 0) throw fault('invalid-money');
    return value;
  }
  function unique(codes) {
    var seen = new Set();
    return codes.map(function (code) { return String(code).trim(); }).filter(function (code) {
      var normalized = code.toUpperCase();
      if (!code || seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
  }
  function normalized(code) { return String(code).toUpperCase(); }
  function groups(config) {
    var values = String(config.groups || '').split(',').map(function (value) { return value.trim(); });
    if (!config.tagPrefix || !config.codePrefix || /[,\s]/.test(config.codePrefix)) throw fault('invalid-groups');
    if (!values.length || values.some(function (value) { return !/^[1-9]\d{0,5}$/.test(value); })) throw fault('invalid-groups');
    return unique(values);
  }
  function group(tags, config) {
    var candidates = (tags || []).filter(function (tag) { return typeof tag === 'string' && tag.indexOf(config.tagPrefix) === 0; });
    if (!candidates.length) return null;
    if (candidates.length !== 1) throw fault('multiple-groups');
    var amount = candidates[0].slice(config.tagPrefix.length);
    if (groups(config).indexOf(amount) === -1) throw fault('unknown-group', { tag: candidates[0] });
    return { tag: candidates[0], code: config.codePrefix + amount, amount: Number(amount) * 100 };
  }
  function price(base, discount) {
    money(base); money(discount);
    var saving = Math.min(base, discount);
    return { original: base, final: base - saving, saving: saving, percent: base ? Math.round(saving * 100 / base) : 0 };
  }
  function range(min, max, discount) {
    money(min); money(max);
    if (min > max) throw fault('invalid-money');
    return { min: price(min, discount), max: price(max, discount) };
  }
  function reference(base, compareAt) {
    money(base);
    return Number.isSafeInteger(compareAt) && compareAt > base ? compareAt : base;
  }
  function percent(original, final) {
    money(original); money(final);
    return original ? Math.max(0, Math.round((original - final) * 100 / original)) : 0;
  }
  function presentation(base, discount, compareAt) {
    var result = price(base, discount);
    var original = reference(base, compareAt);
    return { original: original, final: result.final, percent: percent(original, result.final) };
  }
  function presentationRange(variants, discount) {
    if (!Array.isArray(variants) || !variants.length) throw fault('invalid-money');
    var offers = variants.map(function (variant) { return presentation(variant.price, discount, variant.compareAt); });
    return {
      originalMin: Math.min.apply(null, offers.map(function (offer) { return offer.original; })),
      originalMax: Math.max.apply(null, offers.map(function (offer) { return offer.original; })),
      finalMin: Math.min.apply(null, offers.map(function (offer) { return offer.final; })),
      finalMax: Math.max.apply(null, offers.map(function (offer) { return offer.final; })),
      percentMin: Math.min.apply(null, offers.map(function (offer) { return offer.percent; })),
      percentMax: Math.max.apply(null, offers.map(function (offer) { return offer.percent; }))
    };
  }
  function cartCodes(cart) {
    // Do not reconstruct a potentially incomplete list from Liquid allocations.
    if (!Array.isArray(cart.discount_codes)) throw fault('codes-unavailable');
    if (cart.discount_codes.some(function (entry) { return !entry || typeof entry.code !== 'string' || typeof entry.applicable !== 'boolean'; })) {
      throw fault('codes-unavailable');
    }
    return unique(cart.discount_codes.map(function (entry) { return entry.code; }));
  }
  function signature(items) {
    return JSON.stringify(items.map(function (item) {
      return [String(item.variant_id || item.variantId || item.id), item.quantity, String(item.key)];
    }).sort(function (a, b) { return JSON.stringify(a).localeCompare(JSON.stringify(b)); }));
  }
  function identity(item) {
    var properties = item.properties || {};
    var entries = Object.keys(properties).sort().map(function (key) { return [key, properties[key]]; });
    var sellingPlan = item.sellingPlanId || (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan.id) || null;
    return JSON.stringify([String(item.variant_id || item.variantId || item.id), entries, sellingPlan]);
  }
  function coherent(cart, meta) {
    if (!meta || cart.currency !== meta.currency || cart.total_price !== meta.totalPrice || signature(cart.items) !== signature(meta.items)) {
      throw fault('cart-changed');
    }
    cart.items.forEach(function (line) {
      var item = meta.items.find(function (entry) { return String(entry.key) === String(line.key); });
      if (!item || (line.original_price !== undefined && line.original_price !== item.originalPrice) ||
          (line.final_line_price !== undefined && line.final_line_price !== item.finalLinePrice)) throw fault('cart-changed');
    });
  }
  function plan(cart, meta, config, owned, active) {
    coherent(cart, meta);
    var existing = cartCodes(cart);
    if (active && cart.currency !== config.currency) throw fault('currency-mismatch');
    var ownedSet = new Set((owned || []).map(normalized));
    var external = existing.filter(function (code) { return !ownedSet.has(normalized(code)); });
    var required = [];
    var lines = [];
    if (active) {
      groups(config);
      meta.items.forEach(function (item) {
        var selected = group(item.tags, config);
        if (!selected) return;
        if (item.sellingPlan || item.giftCard || item.bundle) throw fault('unsupported-product');
        var expected = price(item.originalPrice, selected.amount).saving * item.quantity;
        money(expected);
        // Free items do not need a zero-value discount code to be accepted.
        if (!expected) return;
        required.push(selected.code);
        lines.push({ variantId: String(item.variantId), identity: identity(item), key: item.key, quantity: item.quantity,
          code: selected.code, expected: expected, originalPrice: item.originalPrice });
      });
    }
    required = unique(required);
    var target = unique(external.concat(required));
    // Unknown existing code classes conservatively occupy a product/order slot.
    var shipping = new Set((meta.shippingCodes || []).map(normalized));
    var slots = target.filter(function (code) { return !shipping.has(normalized(code)); }).length;
    if (active && slots > 5) throw fault('code-limit', { groups: required, slots: slots, external: external });
    var newlyOwned = required.filter(function (code) {
      return ownedSet.has(normalized(code)) || !existing.some(function (old) { return normalized(old) === normalized(code); });
    });
    return { before: existing, target: target, required: required, external: external,
      owned: newlyOwned, lines: lines,
      changed: JSON.stringify(existing.map(normalized).sort()) !== JSON.stringify(target.map(normalized).sort()) };
  }
  function verify(cart, meta, requested, beforeCart) {
    coherent(cart, meta);
    cartCodes(cart);
    money(cart.total_price); money(cart.total_discount);
    var accepted = new Set(cart.discount_codes.filter(function (entry) { return entry.applicable; }).map(function (entry) { return normalized(entry.code); }));
    requested.required.forEach(function (code) { if (!accepted.has(normalized(code))) throw fault('code-rejected', { code: code }); });
    if (beforeCart) {
      beforeCart.discount_codes.filter(function (entry) { return entry.applicable; }).forEach(function (entry) {
        if (requested.external.some(function (code) { return normalized(code) === normalized(entry.code); }) && !accepted.has(normalized(entry.code))) {
          throw fault('code-conflict', { code: entry.code });
        }
      });
    }
    var savings = 0;
    requested.lines.forEach(function (expected) {
      // Discount application can change the line key. Match variant + quantity
      // and the original key where possible; never sum unrelated line items.
      var candidates = meta.items.filter(function (item) { return identity(item) === expected.identity && item.quantity === expected.quantity; });
      var item = candidates.find(function (item) { return item.key === expected.key; });
      if (!item && candidates.length === 1) item = candidates[0];
      if (!item || item.originalPrice !== expected.originalPrice) throw fault('verification-unavailable');
      var allocated = (item.allocations || []).filter(function (allocation) {
        return allocation.type === 'discount_code' && normalized(allocation.title) === normalized(expected.code);
      }).reduce(function (sum, allocation) { return sum + money(allocation.amount); }, 0);
      if (allocated !== expected.expected || item.finalLinePrice > item.originalPrice * item.quantity - expected.expected) {
        throw fault('verification-unavailable', { code: expected.code });
      }
      savings += allocated;
    });
    money(savings);
    return { savings: savings, total: cart.total_price, totalDiscount: cart.total_discount };
  }
  return Object.freeze({ fault: fault, money: money, unique: unique, groups: groups, group: group,
    price: price, range: range, reference: reference, percent: percent, presentation: presentation, presentationRange: presentationRange,
    cartCodes: cartCodes, signature: signature, identity: identity, coherent: coherent, plan: plan, verify: verify });
});
